import { useMutation, useQueryClient } from 'react-query';
import { AccountProps } from 'services/hooks/account-service';

type Context = { previousAccount: AccountProps[] };
function isContext(value: unknown): value is Context {
  return typeof value === 'object' && value !== null && value.hasOwnProperty('previousAccount');
}

const useDeleteAccount = ({
  deleteQuery,
  queryName,
  onSuccess,
  removeOnSuccess = false
}: {
  deleteQuery: (uuid: string | undefined) => Promise<AccountProps>;
  queryName: string;
  onSuccess?: (response: AccountProps) => void;
  removeOnSuccess?: boolean;
}) => {
  /* The query client is responsible for managing and caching data fetched from the server using React Query. It provides methods to 
  interact with the cache, such as retrieving data, updating data, and invalidating queries. */
  const queryClient = useQueryClient();

  /* The `useMutation` hook is used to create a mutation function that can be used to perform an asynchronous operation, such as deleting 
  an Account. */
  const deleteAccountMutation = useMutation(
    async (uuid?: string) => {
      /* The below code is calling the `deleteQuery` function with the `uuid` parameter and awaiting its response. The `deleteQuery` function is
      expected to be a function that performs the actual deletion of an Account and returns the data. The `data` variable is then assigned the 
      returned value from the `deleteQuery` function, and it is returned from the mutation function. */
      const data = await deleteQuery(uuid);
      return data;
    },
    {
      /* The `onMutate` function is a callback function that is called before the mutation is executed.
      It is used to perform optimistic updates to the query data in the cache. */
      onMutate: async (uuid) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries([queryName]);

        // Snapshot the previous value
        const previousAccount = queryClient.getQueryData<AccountProps[]>([queryName]);

        // Optimistically update to the new value
        if (previousAccount) {
          const data = previousAccount.filter((account) => {
            return account.uuid !== uuid;
          });
          queryClient.setQueryData<AccountProps[]>([queryName], {
            ...previousAccount,
            ...data
          });
        }

        return { previousAccount };
      },
      onSuccess: async (response) => {
        if (removeOnSuccess) {
          /* When deleting a user form the single user page, we want to ensure we have fresh data when being redirected back to the index.
           * However, when deleting from the index we don't want to reload the whole index.
           * It is used to remove all queries with a specific query key from the cache. */
          queryClient.invalidateQueries(queryName);
        }

        /* This is a callback function that is called when the delete Account mutation is successful. It takes the `response` as an argument, 
          which is the data returned from the `deleteQuery` function. */
        if (onSuccess) {
          onSuccess(response);
        }
      },
      onError: (err, variables, context) => {
        console.log(err);
        console.log(variables);
        /* This code block handles the rollback of data in case the mutation (deletion of an Account) fails using the context returned from 
        onMutate. */
        if (isContext(context)) {
          queryClient.setQueryData<AccountProps[]>([queryName], context.previousAccount);
        }
      }
    }
  );

  return deleteAccountMutation;
};

export default useDeleteAccount;
